/**
 * useLiveAppBuild (T29.2.1) — LOCAL-dev-only live build stamp.
 *
 * On a local checkout the version must track the CURRENT commit with no rebuild /
 * restart. The git hook (`version-stamp.mjs --write`) writes the stamp into
 * `apps/web/src/version.local.json` on every commit; the Vite virtual-module
 * plugin (`virtual:shopista-live-app-build`, see vite.config.ts) reads that file
 * and serves it, re-loading on a commit via HMR. This hook surfaces that live
 * stamp to the display so the restored About / 5-tap grid shows the current
 * commit's number on local.
 *
 * Build/deployed: the virtual module is `apply: 'serve'`, so it does NOT exist in
 * a built bundle. The dynamic import is gated on `import.meta.env.DEV` and is
 * never reached (and tree-shaken) in build, so the deployed display falls back to
 * the baked `__APP_BUILD__` — byte-for-byte unchanged. A STATIC import of the
 * virtual module would break the deploy build (absent in CI); the dev-only
 * dynamic import avoids that.
 *
 * Returns `null` until the live read resolves (and always on build), so callers
 * fall back to the baked `__APP_BUILD__` and never render empty.
 */

import { useEffect, useState } from 'react';
import type { AppBuild } from './appVersion';

export function useLiveAppBuild(): AppBuild | null {
  const [live, setLive] = useState<AppBuild | null>(null);

  useEffect(() => {
    // Only under `vite dev` does the virtual module exist. Guard the dynamic
    // import so the built bundle never tries to resolve it.
    if (!import.meta.env.DEV) return;
    let cancelled = false;
    // The virtual id is provided by the serve-only plugin; the `@vite-ignore`
    // keeps Vite from trying to pre-bundle/analyze it statically.
    import(/* @vite-ignore */ 'virtual:shopista-live-app-build')
      .then((mod) => {
        if (!cancelled) setLive((mod.default as AppBuild) ?? null);
      })
      .catch(() => {
        // Plugin absent / read failed — stay null; caller uses the baked stamp.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return live;
}
